import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';

interface TypewriterAnimationProps {
  messages: string[];
  typingSpeed?: number;
  deleteSpeed?: number;
  delayBetweenMessages?: number;
  onComplete?: () => void;
  visible?: boolean;
}

// Smart article detection for medical/clinical documents
const getArticle = (phrase: string): string => {
  if (!phrase) return 'a';

  // Common medical/clinical acronyms and their correct articles
  const acronymsWithAn = [
    'MRI',
    'EKG',
    'ECG',
    'EMR',
    'EHR',
    'ESA',
    'FMLA',
    'ADA',
    'ADHD',
    'OCD',
    'IEP',
    'EEG',
    'ER',
    'ICU',
    'IV',
    'ICD',
    'APRN',
    'RN',
    'LPN',
    'OT',
    'SLP',
  ];
  const acronymsWithA = [
    'HIPAA',
    'PHI',
    'DSM',
    'CPT',
    'SOAP',
    'PTSD',
    'CT',
    'PET',
    'CPAP',
    'PICC',
    'CABG',
    'TURP',
  ];

  // Check if it starts with an acronym
  const firstWord = phrase.split(' ')[0].toUpperCase();
  if (acronymsWithAn.includes(firstWord)) return 'an';
  if (acronymsWithA.includes(firstWord)) return 'a';

  // Check for words with silent H (hour, honest, honor)
  if (/^ho(n|ur)/i.test(phrase)) return 'an';

  // Check for words starting with 'uni' or 'eu' (university, user, euphemism)
  if (/^(uni|eu|use)/i.test(phrase)) return 'a';

  // Check for words starting with 'one' (one-time, one-page)
  if (/^one/i.test(phrase)) return 'a';

  // Default vowel check for first letter
  return /^[aeiou]/i.test(phrase) ? 'an' : 'a';
};

export const TypewriterAnimation: React.FC<TypewriterAnimationProps> = ({
  messages,
  typingSpeed = 80,
  deleteSpeed = 40,
  delayBetweenMessages = 2000,
  onComplete,
  visible = true,
}) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  // Initial delay before starting the typewriter
  useEffect(() => {
    if (visible && !hasStarted) {
      const startTimer = setTimeout(() => {
        setHasStarted(true);
      }, 1000); // 1 second delay before starting
      return () => clearTimeout(startTimer);
    }
    if (!visible) {
      setHasStarted(false);
      setCurrentText('');
      setCurrentMessageIndex(0);
      setIsDeleting(false);
    }
  }, [visible, hasStarted]);

  useEffect(() => {
    if (!hasStarted || !visible || messages.length === 0) return;

    const currentMessage = messages[currentMessageIndex];
    let timeout: NodeJS.Timeout;

    if (!isDeleting) {
      // Typing
      if (currentText !== currentMessage) {
        timeout = setTimeout(() => {
          setCurrentText(currentMessage.slice(0, currentText.length + 1));
        }, typingSpeed);
      } else if (currentMessageIndex < messages.length - 1) {
        // Finished typing - wait then start deleting (but not for the last message)
        timeout = setTimeout(() => {
          setIsDeleting(true);
        }, delayBetweenMessages);
      } else if (onComplete) {
        // Last message typed, call onComplete if provided
        onComplete();
      }
    } else {
      // Deleting
      if (currentText !== '') {
        timeout = setTimeout(() => {
          setCurrentText(currentText.slice(0, -1));
        }, deleteSpeed);
      } else {
        // Finished deleting - move to next message
        setIsDeleting(false);
        setCurrentMessageIndex(prev => prev + 1);
      }
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [
    currentText,
    isDeleting,
    currentMessageIndex,
    messages,
    typingSpeed,
    deleteSpeed,
    delayBetweenMessages,
    onComplete,
    visible,
    hasStarted,
  ]);

  if (!visible || messages.length === 0) return null;

  const currentMessage = messages[currentMessageIndex];
  const isLastMessage = currentMessageIndex === messages.length - 1;
  // Remove exclamation point from last message if present
  const displayMessage = isLastMessage ? currentMessage.replace(/!$/, '') : currentMessage;
  const article = isLastMessage ? '' : getArticle(displayMessage);

  return (
    <Typography
      variant="body2"
      sx={{
        fontSize: '14px',
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        color: 'white',
      }}
    >
      <Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
        New:
      </Box>
      <Box component="span" sx={{ mr: 0.5 }}>
        Generate{article ? ` ${article}` : ''}
      </Box>
      <Box
        component="span"
        sx={{
          borderBottom: '1px solid white',
          fontWeight: 600,
          pb: 0.25,
          display: 'inline-block',
          minWidth: '20px', // Minimum width to prevent collapse when empty
        }}
      >
        {currentText}
        {(currentText.length < currentMessage.length || isDeleting) && (
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              width: '2px',
              height: '14px',
              backgroundColor: 'white',
              marginLeft: '2px',
              animation: 'blink 1s infinite',
              '@keyframes blink': {
                '0%, 49%': { opacity: 1 },
                '50%, 100%': { opacity: 0 },
              },
            }}
          />
        )}
      </Box>
      <Box
        component="span"
        sx={{
          fontWeight: 600,
          display: 'inline-block',
          ml: 0.5,
          visibility:
            currentText.length === displayMessage.length && !isDeleting ? 'visible' : 'hidden',
          animation:
            currentText.length === displayMessage.length && !isDeleting
              ? 'popIn 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
              : 'none',
          '@keyframes popIn': {
            '0%': {
              transform: 'scale(0) rotate(-15deg)',
              opacity: 0,
            },
            '50%': {
              transform: 'scale(1.2) rotate(5deg)',
            },
            '100%': {
              transform: 'scale(1) rotate(0deg)',
              opacity: 1,
            },
          },
        }}
      >
        {isLastMessage ? '!' : '?'}
      </Box>
    </Typography>
  );
};
